import { PanelBase } from "./PanelBase";
import type { GameContext } from "../../GameContext";
import type { CharacterSummary } from "../../controllers/CharacterSelectController";

interface ZoneNode {
  code: string;
  name: string;
  levelRange: string;
  parent: string | null;
  connectionType: "hub" | "ley-bridge" | "portal";
  x: number;
  y: number;
}

interface ZoneGraph {
  hub: string;
  zones: ZoneNode[];
}

/**
 * Per 1.7: "A dedicated full-screen World Map panel shows all 12 zones as
 * nodes connected by lines... each node showing zone name, level range, a
 * lock icon if not yet unlocked, and the player's current location
 * highlighted." This renders Team Sentinel's proposed zone-graph.json
 * (art/world-map/zone-graph.json — a documented design proposal per its
 * own `_note` field, not spec-given fact; confirm with Team Veil before
 * treating the graph shape as final).
 *
 * Deliberately read-only/informational for now: fast-travel itself isn't
 * yet a documented REST action in this client's API surface (movement
 * within a zone is server-authoritative over the WebSocket per 2.8, but
 * *zone-to-zone* travel isn't specified anywhere in Part 3's routes) —
 * this panel shows the map and lock state accurately per 1.7's own rule
 * (locked zones grayed out with level requirement shown) without
 * inventing a travel call the backend doesn't expose yet.
 */
export class WorldMapPanel extends PanelBase {
  private graph: ZoneGraph | null = null;
  private statusEl!: HTMLDivElement;

  constructor(root: HTMLElement, ctx: GameContext, private readonly character: CharacterSummary) {
    super(root, ctx, "world-map-panel");
  }

  protected async refresh(): Promise<void> {
    if (!this.graph) {
      this.el.innerHTML = "<h3>The Twelve Shattered Isles</h3><p>Loading map…</p>";
      try {
        const res = await fetch("/art/world-map/zone-graph.json");
        this.graph = (await res.json()) as ZoneGraph;
      } catch {
        this.el.innerHTML = `<h3>The Twelve Shattered Isles</h3><p class="panel-error">Map data unavailable.</p>`;
        return;
      }
    }
    this.render(this.graph);
  }

  private render(graph: ZoneGraph): void {
    this.el.innerHTML = "<h3>The Twelve Shattered Isles</h3>";

    const wrap = document.createElement("div");
    wrap.className = "map-svg-wrap";

    const playerLevel = this.character.baseLevel;
    const currentZone = this.character.currentZone;

    const byCode = new Map(graph.zones.map((z) => [z.code, z]));
    const minReq = (z: ZoneNode): number => {
      const match = /(\d+)/.exec(z.levelRange);
      return match?.[1] ? parseInt(match[1], 10) : 0;
    };

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("viewBox", "0 0 820 730");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "360");

    // Connection lines first, so nodes draw on top.
    for (const zone of graph.zones) {
      if (!zone.parent) continue;
      const parent = byCode.get(zone.parent);
      if (!parent) continue;
      const line = document.createElementNS(svgNS, "line");
      line.setAttribute("x1", String(parent.x));
      line.setAttribute("y1", String(parent.y));
      line.setAttribute("x2", String(zone.x));
      line.setAttribute("y2", String(zone.y));
      line.setAttribute("class", zone.connectionType === "portal" ? "conn-portal" : "conn-ley-bridge");
      svg.appendChild(line);
    }

    for (const zone of graph.zones) {
      const isHub = zone.code === graph.hub;
      const isCurrent = zone.code === currentZone;
      const locked = !isHub && playerLevel < minReq(zone);

      const g = document.createElementNS(svgNS, "g");
      g.setAttribute("class", `zone-node${locked ? " locked" : ""}`);

      const circle = document.createElementNS(svgNS, "circle");
      circle.setAttribute("cx", String(zone.x));
      circle.setAttribute("cy", String(zone.y));
      circle.setAttribute("r", isHub ? "16" : "11");
      circle.setAttribute(
        "fill",
        isCurrent ? "#f5a93f" : locked ? "#2a3550" : isHub ? "#f5a93f" : "#2f80b8"
      );
      circle.setAttribute("stroke", isCurrent ? "#fbf3e3" : "#0d1420");
      circle.setAttribute("stroke-width", isCurrent ? "3" : "2");
      g.appendChild(circle);

      if (locked) {
        const lock = document.createElementNS(svgNS, "text");
        lock.setAttribute("x", String(zone.x));
        lock.setAttribute("y", String(zone.y + 4));
        lock.setAttribute("text-anchor", "middle");
        lock.setAttribute("font-size", "10");
        lock.textContent = "🔒";
        g.appendChild(lock);
      }

      const label = document.createElementNS(svgNS, "text");
      label.setAttribute("x", String(zone.x));
      label.setAttribute("y", String(zone.y + (isHub ? 30 : 24)));
      label.setAttribute("class", "zone-label");
      label.textContent = zone.name;
      g.appendChild(label);

      const sub = document.createElementNS(svgNS, "text");
      sub.setAttribute("x", String(zone.x));
      sub.setAttribute("y", String(zone.y + (isHub ? 42 : 36)));
      sub.setAttribute("class", "zone-sub");
      sub.textContent = locked ? `Requires Lv ${minReq(zone)}` : zone.levelRange;
      g.appendChild(sub);

      g.addEventListener("click", () => {
        if (locked) {
          this.statusEl.textContent = `${zone.name} is locked — requires level ${minReq(zone)}.`;
          return;
        }
        if (isCurrent) {
          this.statusEl.textContent = `You are already in ${zone.name}.`;
          return;
        }
        // Per 1.7: fast-travel only from Aurelia Spire; elsewhere shows the
        // route but doesn't fast-travel. No backend travel endpoint is
        // wired up yet (see class doc comment) — this reflects the rule
        // without pretending to perform the travel.
        this.statusEl.textContent =
          currentZone === graph.hub
            ? `Route to ${zone.name} confirmed. (Fast-travel action not yet wired to a backend route.)`
            : `Route to ${zone.name} shown. Travel to Aurelia Spire first to fast-travel.`;
      });

      svg.appendChild(g);
    }

    wrap.appendChild(svg);
    this.el.appendChild(wrap);

    const legend = document.createElement("div");
    legend.className = "map-legend";
    legend.innerHTML = `
      <span><span class="swatch" style="background:var(--veil-gold)"></span> Ley-bridge (open world)</span>
      <span><span class="swatch" style="background:#8b5fbf; border-top:2px dashed #8b5fbf; height:0;"></span> Portal (instanced)</span>
    `;
    this.el.appendChild(legend);

    this.statusEl = document.createElement("div");
    this.statusEl.id = "world-map-status";
    this.statusEl.textContent = "Click an unlocked isle.";
    this.el.appendChild(this.statusEl);
  }
}
